import { NgModule } from "@angular/core";
import { MarkdownModule } from "ngx-markdown";
import { AuthGuardService } from "app/shared/auth-guard.service";
import { canDeactivateGuard } from "app/shared/can-deactivate-guard.service";
import { EntityManagerProviderGuard } from "app/shared/entity-manager-guard";
import { SharedModule } from "app/shared/shared.module";
import { InventoryDamageDialogComponent } from "./inventory-damage-dialog.component";
import { InventoryComponent } from "./inventory.component";
import { ProductTypesComponent } from "./product-types.component";

import { ImageModule } from "app/shared/image/image.module";
import { AddInventoryComponent } from "./add-inventory.component";
import { AddonComponent } from "./addon.component";
import { EditFeatureChoiceDialogComponent } from "./edit-feature-choice-dialog.component";
import { EditManufacturerDialogComponent } from "./edit-manufacturer-dialog.component";
import { EditProductDialogComponent } from "./edit-product-dialog.component";

import { FeatureChoiceFinderDialogComponent } from "./feature-choice-finder-dialog.component";
import { FeatureFinderDialogComponent } from "./feature-finder-dialog.component";
import { FeatureComponent } from "./feature.component";
import { InventoryEoqComponent } from "./inventory-eoq.component";
import { InventoryReconcileComponent } from "./inventory-reconcile.component";
import { InventoryLookupComponent } from "./inventory-lookup.component";
import { ManufacturerComponent } from "./manufacturer.component";
import { ProductFinderDialogComponent } from "./product-finder-dialog.component";

import { ProductTypeFinderDialogComponent } from "./product-type-finder-dialog.component";
import { ProductTypeTagComponent } from "./product-type-tag.component";

import { AddonStationComponent } from "./addon-station.component";
import { BoxSizeComponent } from "./box-size.component";
import { EditAddonDialogComponent } from "./edit-addon-dialog.component";
import { EditBoxSizeDialogComponent } from "./edit-box-size-dialog.component";
import { EditFeatureDialogComponent } from "./edit-feature-dialog.component";
import { FeatureKindComponent } from "./feature-kind.component";
import { EditFeatureKindDialogComponent } from "./edit-feature-kind-dialog.component";
import { EditManufacturerProductTypeConfigDialogComponent } from "./edit-manufacturer-product-type-config-dialog.component";
import { ManufacturerFinderDialogComponent } from "./manufacturer-finder-dialog.component";
import { ManufacturerProductTypeConfigComponent } from "./manufacturer-product-type-config.component";
import { ProductTypeComponent } from "./product-type.component";
import { AddFeedColorsDialogComponent } from './add-feed-colors-dialog.component';
import { ProductBuilderDialogComponent } from "./product-builder-dialog.component";
import { ProductSearchComponent } from "./product-search.component";
import { ManufacturerProductTypeFinderDialogComponent } from "./manufacturer-product-type-finder-dialog.component";
import { ProductTypeChangeFeaturesDialogComponent } from "./product-type-change-features-dialog.component";
import { ReferenceManagerComponent } from "./reference-manager.component";
import { Routes } from "@angular/router";
import { InventoryManagerComponent } from "./inventory-manager.component";
import { ApplyCoreChoicesPreviewDialogComponent } from "./apply-core-choices-preview-dialog.component";
import { BulkImageUploadDialogComponent } from "./bulk-image-upload-dialog.component";
import { ReferenceDocComponent } from "./reference-doc.component";
import { PhaseoutDrainComponent } from "./phaseout-drain.component";
import { ProductReplacementDialogComponent } from "./product-replacement-dialog.component";

export const routes: Routes = [
  {
    path: "inventory",
    canActivate: [AuthGuardService],
    children: [
      {
        path: "",
        redirectTo: "manage",
        pathMatch: "full",
      },
      {
        path: "manage",
        canActivate: [AuthGuardService],
        component: InventoryManagerComponent,
        data: { breadcrumb: "Inventory" },
        children: [
          {
            path: "",
            redirectTo: "inventory",
            pathMatch: "full",
          },
          {
            path: "inventory",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: InventoryComponent,
            data: { breadcrumb: "Inventory" },
          },
          {
            path: "inventory-scan",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: InventoryReconcileComponent,
            data: { breadcrumb: "Scan & Reconcile" },
          },
          {
            path: "inventory-lookup",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: InventoryLookupComponent,
            data: { breadcrumb: "Item Lookup" },
          },
          {
            path: "add-inventory",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: AddInventoryComponent,
            data: { breadcrumb: "Add Inventory" },
          },
          {
            path: "inventory-eoq",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: InventoryEoqComponent,
            data: { breadcrumb: "EOQ Calculator" },
          },
        ],
      },
    ],
  },
  {
    path: "product-types",
    component: ProductTypesComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: "feature",
    component: FeatureComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: "add-inventory",
    component: AddInventoryComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },

  {
    path: "references",
    canActivate: [AuthGuardService],
    children: [
      {
        path: "",
        redirectTo: "manage",
        pathMatch: "full",
      },
      {
        path: "manage",
        canActivate: [AuthGuardService],
        component: ReferenceManagerComponent,
        data: { breadcrumb: "References" },
        children: [
          {
            path: "",
            redirectTo: "manufacturers",
            pathMatch: "full",
          },
          {
            path: "manufacturers",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: ManufacturerComponent,
            data: { breadcrumb: "Manufacturers" },
          },
          {
            path: "product-types",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: ProductTypesComponent,
            data: { breadcrumb: "Product Types" },
          },
          {
            path: "feature-types",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: FeatureKindComponent,
            data: { breadcrumb: "Feature Types" },
          },
          {
            path: "features",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: FeatureComponent,
            data: { breadcrumb: "Features" },
          },
          {
            path: "process-stations",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: AddonStationComponent,
            data: { breadcrumb: "Process Stations" },
          },
          {
            path: "addons",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: AddonComponent,
            data: { breadcrumb: "Add-Ons" },
          },
          {
            path: "product-type-tags",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: ProductTypeTagComponent,
            data: { breadcrumb: "Product Type Tags" },
          },
          {
            path: "manufacturer-product-type-configs",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: ManufacturerProductTypeConfigComponent,
            data: { breadcrumb: "Manufacturer Product Configs" },
          },
          {
            path: "phaseout-drain",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: PhaseoutDrainComponent,
            data: { breadcrumb: "Phaseout Drain" },
          },
          {
            path: "box-sizes",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: BoxSizeComponent,
            data: { breadcrumb: "Box Sizes" },
          },
          {
            path: "documentation/reorder-feature-choices",
            canActivate: [AuthGuardService],
            component: ReferenceDocComponent,
            data: { breadcrumb: "Using Reorder Values", mdSrc: "/assets/docs/reorder-feature-choices.md" },
          },
          {
            path: "documentation/product-integrity",
            canActivate: [AuthGuardService],
            component: ReferenceDocComponent,
            data: { breadcrumb: "Understanding Product Integrity", mdSrc: "/assets/docs/product-integrity.md" },
          },
          {
            path: "documentation/manufacturer-feeds",
            canActivate: [AuthGuardService],
            component: ReferenceDocComponent,
            data: { breadcrumb: "Understanding Manufacturer APIs and Feeds", mdSrc: "/assets/docs/manufacturer-feeds.md" },
          },
          {
            path: "product-type/:productTypeId",
            canActivate: [AuthGuardService, EntityManagerProviderGuard],
            canDeactivate: [canDeactivateGuard],
            component: ProductTypeComponent,
            data: { breadcrumb: "Edit Product Type" },
          },
        ],
      },
    ],
  },

  // {
  //   path: "manufacturers",
  //   component: ManufacturerComponent,
  //   canActivate: [AuthGuardService, EntityManagerProviderGuard],
  //   canDeactivate: [canDeactivateGuard],
  // },
  // {
  //   path: "addons",
  //   component: AddonComponent,
  //   canActivate: [AuthGuardService, EntityManagerProviderGuard],
  //   canDeactivate: [canDeactivateGuard],
  // },
  {
    path: "addon-stations",
    component: AddonStationComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: "product-type/:productTypeId",
    component: ProductTypeComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: "product-search",
    component: ProductSearchComponent,
    canActivate: [AuthGuardService, EntityManagerProviderGuard],
    canDeactivate: [canDeactivateGuard],
  },
  // {
  //   path: "product-type-tags",
  //   component: ProductTypeTagComponent,
  //   canActivate: [AuthGuardService, EntityManagerProviderGuard],
  //   canDeactivate: [canDeactivateGuard],
  // },
  // {
  //   path: "manufacturer-product-type-configs",
  //   component: ManufacturerProductTypeConfigComponent,
  //   canActivate: [AuthGuardService, EntityManagerProviderGuard],
  //   canDeactivate: [canDeactivateGuard],
  // },
];

routes.forEach((r: any) => {
  r.data = Object.assign(r.data ?? {}, { icon: "assessment" });
});

@NgModule({
  declarations: [
    InventoryComponent,
    InventoryReconcileComponent,
    InventoryLookupComponent,
    AddInventoryComponent,
    InventoryEoqComponent,
    ProductTypeComponent,
    InventoryDamageDialogComponent,
    FeatureComponent,
    EditProductDialogComponent,
    EditFeatureChoiceDialogComponent,
    ProductFinderDialogComponent,
    ManufacturerComponent,
    ProductTypesComponent,
    EditAddonDialogComponent,
    EditManufacturerDialogComponent,
    AddonComponent,
    AddonStationComponent,

    FeatureChoiceFinderDialogComponent,
    FeatureFinderDialogComponent,
    ProductTypeFinderDialogComponent,
    ProductTypeTagComponent,

    EditFeatureDialogComponent,
    FeatureKindComponent,
    EditFeatureKindDialogComponent,

    ManufacturerProductTypeConfigComponent,
    EditManufacturerProductTypeConfigDialogComponent,
    ManufacturerFinderDialogComponent,
    ProductBuilderDialogComponent,
    AddFeedColorsDialogComponent,
    ProductSearchComponent,
    ManufacturerProductTypeFinderDialogComponent,
    ProductTypeChangeFeaturesDialogComponent,
    ReferenceManagerComponent,
    InventoryManagerComponent,
    ApplyCoreChoicesPreviewDialogComponent,
    BoxSizeComponent,
    PhaseoutDrainComponent,
    ProductReplacementDialogComponent,
    EditBoxSizeDialogComponent,
    BulkImageUploadDialogComponent,
    ReferenceDocComponent,
  ],
  imports: [SharedModule, ImageModule, MarkdownModule.forChild()],
  exports: [],
})
export class InventoryModule { }
